import { Document, Page, View, Text } from "@react-pdf/renderer";
import type { CityReportStation } from "@/components/city-review/types";
import { CoverPage } from "./CoverPage";
import { PdfFooter } from "./PdfFooter";
import { SportDistributionBar } from "./SportDistributionBar";
import { StationsSummaryTable } from "./StationsSummaryTable";
import { StationBlock, type StationBlockComment } from "./StationBlock";
import { AppendixSection } from "./AppendixSection";
import { palette, styles as baseStyles } from "./styles";

/**
 * Shape of the bundled payload returned by the `getReportPdfData` admin tRPC
 * procedure. Defined locally rather than imported from the router so this
 * component tree can be referenced from anywhere without dragging in the
 * server type graph.
 */
export interface GetReportPdfData {
  collection: {
    id: string;
    title: string;
    client_name: string | null;
    logo_url: string | null;
    hub_size: number | null;
    closed: boolean;
  };
  stations: CityReportStation[];
  comments: Record<string, StationBlockComment[]>;
  sportsDistribution: Array<{ sport: string; count: number }>;
  generatedAt: Date;
}

interface CityReportPdfDocumentProps {
  data: GetReportPdfData;
  photoDataUrls: Record<string, string>;
  clientLogoDataUrl: string | null;
}

function computeKpis(stations: CityReportStation[]) {
  let approved = 0;
  let needs_change = 0;
  let rejected = 0;
  let pending = 0;
  for (const s of stations) {
    switch (s.city_verdict) {
      case "approve":
        approved += 1;
        break;
      case "needs_change":
        needs_change += 1;
        break;
      case "reject":
        rejected += 1;
        break;
      default:
        pending += 1;
        break;
    }
  }
  return {
    approved,
    needs_change,
    rejected,
    pending,
    total: stations.length,
  };
}

export function CityReportPdfDocument({
  data,
  photoDataUrls,
  clientLogoDataUrl,
}: CityReportPdfDocumentProps) {
  const cityName = data.collection.client_name ?? data.collection.title;
  const kpis = computeKpis(data.stations);
  const sortedStations = [...data.stations].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );

  return (
    <Document
      title={`Equip Report — ${cityName}`}
      author="Equip Sport"
      creator="Equip Sport"
      producer="Equip Sport"
    >
      <Page size="A4" style={baseStyles.coverPage}>
        <CoverPage
          clientLogoDataUrl={clientLogoDataUrl}
          title={cityName}
          generatedDate={data.generatedAt}
          kpis={kpis}
        />
      </Page>

      <Page size="A4" style={baseStyles.contentPage}>
        <View>
          <Text style={baseStyles.sectionTitle}>Sport distribution</Text>
          <Text style={baseStyles.sectionSubtitle}>
            Lockers planned per sport across all proposed stations.
          </Text>
          <SportDistributionBar data={data.sportsDistribution} />
        </View>

        <View style={{ marginTop: palette.spacing.xl }}>
          <Text style={baseStyles.sectionTitle}>Proposed stations</Text>
          <StationsSummaryTable stations={sortedStations} />
        </View>

        <View style={{ marginTop: palette.spacing.xl }}>
          <Text style={baseStyles.sectionTitle}>Station details</Text>
          {sortedStations.map((station) => (
            <StationBlock
              key={station.id}
              station={station}
              photoDataUrls={photoDataUrls}
              comments={data.comments[station.id] ?? []}
            />
          ))}
        </View>

        <AppendixSection />

        <PdfFooter cityName={cityName} />
      </Page>
    </Document>
  );
}
